import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPublicProjects } from "../../api/projects";
import Placeholder from "../images/placeholder.png";
import { FadeIn } from "../utils/FadeIn";
import { PromangLogo } from "../utils/PromangLogo";

/**
 * Home-side "Prosjekter"-seksjon.
 * Viser inntil MAX_VISIBLE ferdige prosjekter hentet fra Promang og lenker til
 * fullstendig liste på /om-oss/prosjekter. Tom-state + pen loading-skjelett.
 * Badgen viser "Synk fra" + Promang-logo, og seksjonen signeres nederst med
 * "Powered by Promang".
 */
const MAX_VISIBLE = 3;

/** Samme ferdig-sjekk som brukes på prosjektlista/detaljsida. */
function isProjectCompleted(p) {
  if (!p) return false;
  const status = String(
    p.status || p.state || p.progress_status || "",
  ).toLowerCase();
  if (
    ["completed", "finished", "done", "ferdig", "levert", "closed", "delivered"].includes(
      status,
    )
  ) {
    return true;
  }
  if (p.completed === true || p.is_completed === true) return true;
  if (p.finished_at || p.delivered_at || p.completed_at) return true;
  if (p.end_date) {
    const end = new Date(p.end_date);
    if (!Number.isNaN(end.getTime()) && end.getTime() <= Date.now()) return true;
  }
  return false;
}

function getImageUrl(p) {
  if (p?.public_main_image?.file?.url) return p.public_main_image.file.url;
  if (p?.public_main_image?.file_url) return p.public_main_image.file_url;
  if (p?.public_main_image?.url) return p.public_main_image.url;
  return null;
}

function formatYear(d) {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
}

function getDateRange(p) {
  const s = formatYear(p?.start_date);
  const e = formatYear(p?.end_date);
  if (s && e && s !== e) return `${s}–${e}`;
  return s || e || "";
}

function getStatusTag(p) {
  if (!isProjectCompleted(p)) return null;
  return p?.end_date ? { label: "Levert" } : { label: "Ferdig" };
}

export default function FeaturedProjects() {
  const [projects, setProjects] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetchPublicProjects({
          per_page: 24,
          sortBy: "created_at",
          sort: "DESC",
        });
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setTotalCount(list.length);
        // Prioriter ferdige prosjekter — fall tilbake på hele lista hvis ingen
        // er flagget ferdig (f.eks. hvis Promang ikke har status-feltet satt).
        const done = list.filter(isProjectCompleted);
        const base = done.length > 0 ? done : list;
        setProjects(base.slice(0, MAX_VISIBLE));
      } catch (e) {
        console.error("FeaturedProjects load error:", e);
        if (!cancelled) setError("Kunne ikke hente prosjekter nå.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Skjul hele seksjonen hvis vi har feil og ingen fallback-innhold
  if (!isLoading && error && projects.length === 0) {
    return null;
  }

  return (
    <section className="sec tint" id="prosjekter">
      <div className="wrap">
        <div className="sec-head">
          <div className="sec-tag">02 / Prosjekter</div>
          <h2>
            Et utvalg av <em>det vi har levert.</em>
          </h2>

          {/* Synk-badge: "Synk fra [Promang-logo]" */}
          <div
            className="source-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              whiteSpace: "nowrap",
            }}
            aria-label="Synkroniseres fra Promang"
          >
            <span className="pulse" />
            <span style={{ fontFamily: "var(--mono)", fontSize: 11 }}>
              Synk fra
            </span>
            <PromangLogo height={14} color="var(--airon-3)" />
          </div>
        </div>

        {isLoading ? (
          <div className="projects-grid" aria-hidden="true">
            {Array.from({ length: MAX_VISIBLE }).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="project"
                style={{ opacity: 0.55 }}
              >
                <div
                  className="thumb"
                  style={{
                    background:
                      "linear-gradient(90deg, var(--bg-2) 25%, var(--bg-3) 50%, var(--bg-2) 75%)",
                    backgroundSize: "200% 100%",
                    animation: "airon-shimmer 1.4s linear infinite",
                  }}
                />
                <div className="body">
                  <div
                    className="ttl"
                    style={{
                      height: 18,
                      background: "var(--bg-3)",
                      borderRadius: 6,
                      marginBottom: 12,
                      width: "68%",
                    }}
                  />
                  <div
                    style={{
                      height: 12,
                      background: "var(--bg-2)",
                      borderRadius: 6,
                      width: "90%",
                      marginBottom: 6,
                    }}
                  />
                  <div
                    style={{
                      height: 12,
                      background: "var(--bg-2)",
                      borderRadius: 6,
                      width: "70%",
                    }}
                  />
                </div>
              </div>
            ))}
            <style>{`
              @keyframes airon-shimmer {
                0% { background-position: 200% 0; }
                100% { background-position: -200% 0; }
              }
            `}</style>
          </div>
        ) : projects.length === 0 ? (
          <div
            style={{
              padding: 28,
              background: "#fff",
              border: "1px dashed var(--line)",
              borderRadius: 14,
              color: "var(--muted)",
              fontFamily: "var(--mono)",
              fontSize: 12,
              letterSpacing: "0.04em",
            }}
          >
            Ingen ferdige prosjekter registrert i Promang ennå.
          </div>
        ) : (
          <div className="projects-grid">
            {projects.map((project) => {
              const imageUrl = getImageUrl(project);
              const title = project.public_title || `Prosjekt ${project.id}`;
              const description = project.public_description || "";
              const dateRange = getDateRange(project);
              const status = getStatusTag(project);

              return (
                <FadeIn key={project.id}>
                  <Link
                    to={`/om-oss/prosjekter/${project.id}`}
                    className="project"
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                      cursor: "pointer",
                      display: "block",
                    }}
                  >
                    <div
                      className="thumb"
                      data-label={`[ ${(title || "BILDE").toUpperCase()} ]`}
                      style={
                        imageUrl
                          ? {
                              backgroundImage: `url(${imageUrl})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : undefined
                      }
                    >
                      {imageUrl ? (
                        <style>{`
                          .project .thumb[style*="background-image"]::before { display: none; }
                        `}</style>
                      ) : (
                        <img
                          src={Placeholder}
                          alt=""
                          style={{ display: "none" }}
                          aria-hidden="true"
                        />
                      )}
                      {status ? (
                        <span className="tag">{status.label}</span>
                      ) : null}
                    </div>
                    <div className="body">
                      <div className="ttl">{title}</div>
                      {description ? (
                        <p
                          style={{
                            margin: "0 0 12px",
                            color: "var(--ink-2)",
                            fontSize: 14,
                            lineHeight: 1.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {description}
                        </p>
                      ) : null}
                      <div className="meta-row">
                        <span>
                          <b>{project.public_client || "Referanse"}</b>
                        </span>
                        <span>{dateRange || "—"}</span>
                      </div>
                    </div>
                  </Link>
                </FadeIn>
              );
            })}
          </div>
        )}

        {/* Footer-rad: "Se alle prosjekter" + Powered-by Promang */}
        <div
          style={{
            marginTop: 28,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <Link to="/om-oss/prosjekter" className="btn ghost">
            Se alle prosjekter
            {totalCount > MAX_VISIBLE ? ` (${totalCount})` : ""}{" "}
            <span className="arr">→</span>
          </Link>

          <a
            href="https://promang.no"
            target="_blank"
            rel="noreferrer noopener"
            title="Powered by Promang"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              color: "var(--muted)",
              textDecoration: "none",
              fontFamily: "var(--mono)",
              fontSize: 11,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            <span>Powered by</span>
            <PromangLogo height={16} color="var(--ink-2)" />
          </a>
        </div>
      </div>
    </section>
  );
}
